import { Link } from 'react-router'
import { siteNav } from '#app/components/site-nav.ts'
import { ThemeSwitch } from '#app/routes/resources/theme-switch.tsx'
import { cn } from '#app/utils/misc.tsx'
import { useOptionalRequestInfo } from '#app/utils/request-info.ts'
import { THEME_SWITCHER } from '#app/utils/theme-mode.ts'

/**
 * The public footer: the legal pages (nothing else links to them) and, when the
 * theme is user-choosable, the theme switch. Shared by every pre-auth surface —
 * marketing, the customer wizard, the prescriber decision portal and the
 * embedded Shopify Admin app. Post-auth the switch lives in the staff sidebar
 * footer instead, so the staff console doesn't render this.
 *
 * The switch is behind `THEME_SWITCHER` (utils/theme-mode.ts): while a theme is
 * forced, the control is not rendered at all rather than rendered inert.
 */
const footerNav = siteNav

/**
 * `default` — the hairline rule under a page, links left, theme switch right.
 * Marketing and the embedded Shopify app keep it: the Admin iframe has its own
 * chrome and a painted band would fight it.
 *
 * `brand` — dimplecolor.com's own footer: a solid brown band running
 * edge-to-edge, links stacked in one centred column. Used by the two surfaces a
 * customer or prescriber actually lands on from an email, where the page should
 * read as the store's, not as an app's.
 *
 * ⚠ NO CALLER as of 2026-08-02 — SiteHeader replaced this band on the customer
 * and prescriber surfaces, and the two layouts that still render SiteFooter
 * (marketing, embedded Shopify app) both take `default`. Editing this variant
 * changes nothing on screen; check for a caller before you trust a preview.
 */
type SiteFooterVariant = 'default' | 'brand'

export function SiteFooter({
	className,
	navClassName,
	newTab = false,
	variant = 'default',
}: {
	className?: string
	/**
	 * Width and gutters for the link row. Defaults to the wide marketing measure,
	 * which is wrong anywhere the surface above it is narrower — the wizard is
	 * `max-w-xl px-5`, so its footer links started outdented from the content
	 * they sit under. Pass the host surface's own measure to line them up.
	 */
	navClassName?: string
	/**
	 * Open the legal pages in a new tab instead of navigating in place. Required
	 * inside the embedded Shopify app: an in-place nav would take the Admin
	 * iframe to a route with no App Bridge session, stranding the user outside
	 * the app chrome with no way back.
	 */
	newTab?: boolean
	/** See {@link SiteFooterVariant}. */
	variant?: SiteFooterVariant
}) {
	// `useOptionalRequestInfo`: this renders inside error boundaries too, and a
	// root-level one has no loader data. The switch is dropped rather than
	// rendered switch-less there — it reads the request info itself (for the
	// progressive-enhancement `redirectTo`) and would throw.
	const requestInfo = useOptionalRequestInfo()
	const brand = variant === 'brand'
	// `justify-between` needs a right-side element to justify against. When the
	// theme switcher is off (embedded Shopify app forces a theme, so THEME_
	// SWITCHER is false), the links pinned to the left of an empty row read as
	// mis-aligned. Centre them instead when there is nothing on the right.
	const hasRight = !brand && THEME_SWITCHER && !!requestInfo
	// Brand link type is the storefront's own: `.footer_bottom-content` is 10px /
	// line-height 1.5 / uppercase. `text-[10px]` rather than `text-xs` to hit it
	// exactly — this band is meant to read as the same footer, not a near miss.
	const linkClass = brand
		? 'text-brand-footer-foreground/70 hover:text-brand-footer-foreground text-[10px] leading-normal tracking-[0.1em] uppercase'
		: 'text-muted-foreground hover:text-foreground text-sm'
	return (
		<footer
			className={cn(
				// `--brand-footer` is dimplecolor.com's own #2b0d00, not a token from
				// the brown ramp — see styles/tailwind.css. It holds in dark too.
				brand ? 'bg-brand-footer text-brand-footer-foreground' : 'border-t',
				className,
			)}
		>
			<nav
				className={cn(
					'mx-auto flex w-full',
					brand
						? // `py-4`, not the roomier band this started as: dimplecolor's own
							// footer sets the copy tight to the top of the colour block, and
							// anything looser reads as a slab bolted under the page.
							'max-w-5xl flex-col items-center gap-4 px-6 py-4'
						: hasRight
							? 'max-w-5xl items-center justify-between px-6 py-4'
							: 'max-w-5xl items-center justify-center px-6 py-4',
					navClassName,
				)}
			>
				{/*
				 * Brand: one centred column on a phone, one centred row from `sm` up.
				 * A flex row rather than a 3-up grid — the grid's tracks are as wide as
				 * the measure, so on the prescriber portal (no `navClassName`, so the
				 * full `max-w-5xl`) three short links would be flung to the far corners.
				 * Flex keeps them grouped at the centre at every width.
				 */}
				<div
					className={cn(
						'flex',
						brand
							? 'flex-col items-center gap-3 sm:flex-row sm:gap-[30px]'
							: 'items-center gap-6',
					)}
				>
					{footerNav.map((item) =>
						newTab ? (
							<a
								key={item.to}
								href={item.to}
								target="_blank"
								rel="noreferrer"
								className={linkClass}
							>
								{item.label}
							</a>
						) : (
							<Link
								key={item.to}
								to={item.to}
								prefetch="intent"
								className={linkClass}
							>
								{item.label}
							</Link>
						),
					)}
				</div>
				{THEME_SWITCHER && requestInfo ? (
					<ThemeSwitch userPreference={requestInfo.userPrefs.theme} />
				) : null}
			</nav>
		</footer>
	)
}
